import { useEffect, useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, inr, shortInr, fmtDate, download } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const GROUPS = [["party", "Party"], ["ledger", "Ledger"], ["bank", "Bank"], ["date", "Date"], ["month", "Month"], ["direction", "Debit/credit"], ["invoice", "Invoice"], ["mode", "Payment mode"], ["business", "Business/personal"]];
const today = new Date().toISOString().slice(0, 10);
const ago = (d) => new Date(Date.now() - d * 864e5).toISOString().slice(0, 10);

function Summary() {
  const [g, setG] = useState("party");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/reports/summary", { params: { group_by: g, date_from: from, date_to: to } }).then((r) => setRows(r.data)); }, [g, from, to]);
  const preset = (d) => { setFrom(d === 0 ? today : ago(d)); setTo(today); };
  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={g} onValueChange={setG}><SelectTrigger className="w-48" data-testid="report-group-select"><SelectValue /></SelectTrigger>
          <SelectContent>{GROUPS.map(([a, b]) => <SelectItem key={a} value={a}>Group by {b}</SelectItem>)}</SelectContent></Select>
        {[["Daily", 0], ["Weekly", 7], ["Monthly", 30]].map(([l, d]) => <Button key={l} size="sm" variant="outline" onClick={() => preset(d)} data-testid={`report-preset-${l.toLowerCase()}`}>{l}</Button>)}
        <Button size="sm" variant="ghost" onClick={() => { setFrom(""); setTo(""); }}>All time</Button>
        <Input type="date" className="w-40" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" />
        <Input type="date" className="w-40" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" />
      </div>
      <div className="panel mb-4 h-72 p-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows.slice(0, 12).map((r) => ({ ...r, debit: Number(r.debit), credit: Number(r.credit) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="key" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval={0} angle={-15} height={50} />
            <YAxis tickFormatter={shortInr} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={70} />
            <Tooltip formatter={(v) => inr(Number(v).toFixed(2))} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
            <Legend /><Bar dataKey="credit" fill="hsl(var(--emerald))" radius={[4, 4, 0, 0]} /><Bar dataKey="debit" fill="hsl(var(--chart-5))" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="panel overflow-x-auto"><table className="w-full text-sm"><thead className="text-left text-xs text-muted-foreground"><tr>{["Group", "Count", "Debits", "Credits", "Net"].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => <tr key={r.key} className="border-t" data-testid="report-row"><td className="p-3">{r.key}</td><td className="num p-3">{r.count}</td><td className="num p-3 text-rose-500">{inr(r.debit)}</td><td className="num p-3 text-emerald-500">{inr(r.credit)}</td><td className="num p-3">{inr(r.net)}</td></tr>)}</tbody></table></div>
    </div>
  );
}

function PartyStatement() {
  const { parties } = useApp();
  const [pid, setPid] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { if (pid) api.get("/reports/party-statement", { params: { party_id: pid } }).then((r) => setD(r.data)); }, [pid]);
  return (
    <div>
      <Select value={pid} onValueChange={setPid}><SelectTrigger className="mb-4 w-72" data-testid="party-statement-select"><SelectValue placeholder="Choose party" /></SelectTrigger>
        <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
      {d && <div className="panel p-5" data-testid="party-statement">
        <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4 text-sm">{[["Opening", d.opening], ["Debits", d.debits], ["Credits", d.credits], ["Closing", d.closing]].map(([k, v]) => <div key={k}><div className="label-xs">{k}</div><div className="num">{inr(v)}</div></div>)}</div>
        <div className="mb-3 text-xs text-muted-foreground">{d.note}</div>
        <table className="w-full text-xs"><tbody>{d.rows.map((r, i) => <tr key={i} className="border-t"><td className="num p-2">{fmtDate(r.date)}</td><td className="max-w-[300px] truncate p-2 font-mono">{r.narration}</td><td className="p-2">{r.bill_no}</td><td className="num p-2 text-rose-500">{Number(r.debit) ? inr(r.debit) : ""}</td><td className="num p-2 text-emerald-500">{Number(r.credit) ? inr(r.credit) : ""}</td><td className="num p-2">{inr(r.balance)}</td></tr>)}</tbody></table>
      </div>}
    </div>
  );
}

function Monthly() {
  const [m, setM] = useState([]);
  const [rec, setRec] = useState([]);
  useEffect(() => { api.get("/reports/monthly").then((r) => setM(r.data)); api.get("/reports/recurring").then((r) => setRec(r.data)); }, []);
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <div className="panel p-5"><h3 className="mb-3 font-semibold">Monthly bank-to-bank comparison</h3>
        {m.map((x) => <div key={x.month} className="mb-3"><div className="label-xs mb-1">{x.month}</div>{x.banks.map((b) => <div key={b.bank} className="flex justify-between border-b py-1.5 text-xs" data-testid="monthly-row"><span>{b.bank}</span><span className="num"><span className="text-rose-500">Dr {inr(b.debit)}</span> · <span className="text-emerald-500">Cr {inr(b.credit)}</span></span></div>)}</div>)}</div>
      <div className="panel p-5"><h3 className="mb-3 font-semibold">Recurring transactions</h3>
        {rec.length === 0 && <div className="text-sm text-muted-foreground">Needs at least two similar entries for a party.</div>}
        {rec.map((r) => <div key={r.party + r.direction} className="flex justify-between border-b py-2 text-xs" data-testid="recurring-row"><span>{r.party} <span className="text-muted-foreground">· {r.direction} · {r.occurrences}× · {r.months.join(", ")}</span></span><span className="num">~{inr(r.typical_amount)}</span></div>)}</div>
    </div>
  );
}

export default function Reports() {
  const [day, setDay] = useState(today);
  return (
    <div>
      <PageHeader title="Reports & Analytics" sub="Daily, weekly, monthly and custom ranges. Duplicates are excluded from totals.">
        <Input type="date" className="w-40" value={day} onChange={(e) => setDay(e.target.value)} data-testid="daily-report-date" />
        <Button variant="outline" onClick={() => download("/reports/daily.xlsx", { params: { day } })} data-testid="download-daily-excel"><FileSpreadsheet className="mr-2 h-4 w-4" />Daily Excel</Button>
      </PageHeader>
      <Tabs defaultValue="summary">
        <TabsList><TabsTrigger value="summary" data-testid="tab-summary">Grouped summary</TabsTrigger><TabsTrigger value="party" data-testid="tab-party-statement">Party statement</TabsTrigger><TabsTrigger value="monthly" data-testid="tab-monthly">Monthly & recurring</TabsTrigger></TabsList>
        <TabsContent value="summary"><Summary /></TabsContent>
        <TabsContent value="party"><PartyStatement /></TabsContent>
        <TabsContent value="monthly"><Monthly /></TabsContent>
      </Tabs>
    </div>
  );
}
